import { Button, Tooltip } from "@light-technology/components";
import { type PortableTextBlock, toPlainText } from "@portabletext/react";
import { Check, Link01, Mail01 } from "@untitledui/icons";
import { useState } from "react";
import { Link } from "react-router";
import type { PlaybookContentAuthor } from "../../state/api.types";
import { Role } from "../../state/api.types";
import { useAuthHasRole } from "../../state/hooks";
import { toSlug } from "../../utilities/slug";
import { SkeletonImage, SkeletonLine } from "./skeletons";

export type PlaybookContentLayoutProps = {
  children: React.ReactNode;
  title?: string;
  author?: PlaybookContentAuthor;
  authorSkeleton?: boolean;
  body?: unknown[];
  headingsSkeleton?: boolean;
};

const makeHeadings = (body: unknown[] | undefined) => {
  if (!body) return [];
  if (!Array.isArray(body)) return [];
  return body
    .filter(
      (block: unknown): block is PortableTextBlock =>
        typeof block === "object" &&
        block !== null &&
        (block as PortableTextBlock)._type === "block" &&
        !!(block as PortableTextBlock).style &&
        (block as PortableTextBlock).style === "h2",
    )
    .map((block) => {
      const text = toPlainText(block);
      return {
        text,
        slug: toSlug(text),
      };
    });
};

export const PlaybookContentLayout: React.FC<PlaybookContentLayoutProps> = ({
  children,
  title,
  author,
  authorSkeleton,
  body,
  headingsSkeleton,
}) => {
  const isAdmin = useAuthHasRole(Role.OrgAdmin);
  const headings = makeHeadings(body);
  const [copied, setCopied] = useState(false);

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href).then(() => {
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    });
  };

  const showAuthor = authorSkeleton ? true : !!author;
  const showHeadings = headingsSkeleton ? true : headings.length > 0;

  return (
    <div className="flex flex-col items-center gap-32 @page-md:flex-row @page-md:items-start @page-md:gap-32">
      <div className="flex-1">{children}</div>
      <aside className="sticky top-8 flex w-full shrink-0 flex-col @page-sm:w-70">
        <div className="flex flex-col gap-xl rounded-2xl bg-tertiary px-md py-3xl">
          {showAuthor && (
            <div className="flex items-center justify-center gap-lg border-b border-secondary pb-3xl">
              {author ? (
                <img
                  src={author.photo?.url}
                  alt={author.name}
                  className="h-12 w-12 shrink-0 rounded-full bg-tertiary object-cover"
                />
              ) : (
                <SkeletonImage
                  className="h-12 w-12 shrink-0 rounded-full"
                  subtle
                />
              )}
              <div className="flex max-w-40 flex-col">
                <span className="text-md font-semibold">
                  {author ? (
                    author.name
                  ) : (
                    <SkeletonLine className="w-24" subtle />
                  )}
                </span>
                <span className="text-md text-secondary">
                  {author ? (
                    author.job_title
                  ) : (
                    <SkeletonLine className="w-24" subtle />
                  )}
                </span>
              </div>
            </div>
          )}
          {showHeadings && (
            <ul className="flex flex-col gap-md border-b border-secondary pb-xl">
              {headings.map((heading) => (
                <li key={heading.slug}>
                  <a
                    href={`#${heading.slug}`}
                    className="block truncate rounded-lg px-md py-xs text-sm font-medium hover:bg-quaternary"
                  >
                    {heading.text}
                  </a>
                </li>
              ))}
              {!headings.length && headingsSkeleton && (
                <>
                  <SkeletonLine className="w-3/4" subtle />
                  <SkeletonLine className="w-2/3" subtle />
                  <SkeletonLine className="w-4/5" subtle />
                  <SkeletonLine className="w-1/2" subtle />
                </>
              )}
            </ul>
          )}
          <div className="flex flex-col gap-lg">
            <div className="flex items-center justify-center gap-lg">
              <Tooltip title="Copy link" arrow>
                <Button
                  color="secondary"
                  size="md"
                  iconLeading={copied ? Check : Link01}
                  onClick={handleCopyLink}
                />
              </Tooltip>
              <Tooltip title="Mail link" arrow>
                <Button
                  color="secondary"
                  size="md"
                  iconLeading={Mail01}
                  href={`mailto:?subject=${encodeURIComponent(title ?? "")}&body=${encodeURIComponent(window.location.href)}`}
                  target="_blank"
                />
              </Tooltip>
            </div>
            <div className="text-center text-xs text-tertiary">
              Only teammates can view.
              {isAdmin && (
                <>
                  {" "}
                  <Link to="/settings/team" className="underline">
                    Invite teammate
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};
